<template>
  <div class="flex p-4 bg-primary border-b" @click="open = true">
    <div class="flex items-center justify-between w-full cursor-pointer">
      <div class="flex items-center justify-start grow gap-2">
        <div class="text-primary text-style-lg font-semibold leading-[28px]">
          {{ title }}
        </div>
        <Badge
          v-if="selectedOptions(modelValue).length > 0"
          :label="selectedOptions(modelValue).length"
          type="modern"
          size="lg"
          number
        />
      </div>
      <div class="flex items-center justify-center">
        <Icon name="chevron-down" class="ml-1 fg-secondary" size="xl" />
      </div>
    </div>
    <Dialog
      @update:open="open = !open"
      :open="open"
      :showCancel="false"
      :showConfirm="false"
      :showFooter="true"
      variant="default"
      size="full"
      side="bottom"
    >
      <template #header>
        <DrawerHeader
          :title="title"
          v-bind="{
            variant: 'tertiary',
            type: 'gray',
            size: 'xl',
          }"
          class="bg-primary max-md:rounded-none border-b border-secondary"
        />
      </template>
      <template #content>
        <div class="bg-primary grow overflow-y-auto flex flex-col gap-4">
          <div class="flex flex-col grow border-b border-secondary">
            <div
              class="px-3 py-3 border-b border-secondary flex flex-col gap-3 items-end"
            >
              <Search
                :placeholder="t('components.inputs.type_to_search')"
                @update:modelValue="searchTerm = $event"
              />
              <Checkbox
                v-if="type === 'space' && showIncludeAssets"
                size="md"
                :checked="includeAssets"
                :label="t('components.filters.spaces_or_assets.include_assets')"
                @click="handleUpdateIncludeAssets(includeAssets)"
              />
            </div>
            <div class="px-3 pt-2 pb-3 bg-secondary grow">
              <TreeNode
                v-if="type === 'asset_group'"
                :key="'no_asset_group'"
                :node="{
                  name: t('components.filters.spaces_or_assets.no_asset_group'),
                  id: 'no_asset_group',
                  identifier: null,
                  type: type,
                  children: [],
                }"
                :selectedNodeIds="modelValue"
                @update:filters="handleUpdateFilters"
                :type="type"
              />
              <TreeNode
                v-for="node in tree"
                :key="node.id"
                :node="node"
                :level="1"
                :breadcrumb="node.name"
                :selectedNodeIds="modelValue"
                :shouldAutoOpen="node.shouldAutoOpen"
                :searchTerm="searchTerm"
                :type="type"
                @update:filters="handleUpdateFilters"
              />
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div
          class="flex max-sm:flex-col gap-3 p-4 md:p-0 bg-primary md:bg-none"
        >
          <Button
            variant="secondary"
            type="gray"
            size="lg"
            class="grow"
            :label="t('components.buttons.clear_selection')"
            @click="handleClear"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
// Vue and Vue-related imports
import { ref } from "vue";
import { useI18n } from "vue-i18n";

// Shared components
import { TreeNode } from "@/SharedComponents/Filters/Tree";
import { Button } from "@/SharedComponents/Buttons";
import { Dialog } from "@/SharedComponents/Dialog";
import { DrawerHeader } from "@/SharedComponents/Drawer";
import { Search } from "@/SharedComponents/Filters/Drawer/Tree";
import { Badge } from "@/SharedComponents/Badges";
import { Checkbox } from "@/SharedComponents/Checkboxes";
import { Icon } from "@/SharedComponents/Icon";

// Composables
import { useSpacesAndAssetsTree } from "@/Composables/useSpacesAndAssetsTree";

// Define emitted events
const emit = defineEmits([
  "update:modelValue",
  "update:assets",
  "update:includeAssets",
]);

// Define component props
const props = defineProps({
  buildings: { type: Array, required: true },
  type: { type: String, required: true },
  modelValue: { type: Object, default: () => ({}) },
  title: { type: String, required: true },
  includeAssets: { type: Boolean, default: false },
  showIncludeAssets: { type: Boolean, default: true },
});

// Initialize reactive variables and composables
const open = ref(false);
const { t } = useI18n();

// Use the spaces and assets tree composable
const {
  handleClear,
  handleUpdateFilters,
  handleUpdateIncludeAssets,
  searchTerm,
  tree,
  selectedOptions,
} = useSpacesAndAssetsTree(props.buildings, props.type, props.modelValue, emit);
</script>
